import React, { useMemo } from "react";
import styled from "@emotion/styled";

import * as tokens from "../../design-tokens";

import { Button } from "../Button";
import { formatDateToString } from "./helpers";

const TEST_ID = "experience-ui-components--DateCalendar--SingleDaySlot";
const testIdMap = {
  primary: "selected",
  secondary: "available",
};

const SingleDaySlot = ({
  selectedDate,
  onSelectedDate,
  singleDate,
  availableDates,
  disabledPastDays,
  activeMonth,
  isMobile,
}: SingleTimeSlotProps) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const formattedSingleDate = formatDateToString(singleDate);

  const isSelected =
    !!selectedDate &&
    formattedSingleDate === formatDateToString(new Date(selectedDate));

  const isPastDay = disabledPastDays && singleDate.getTime() < today.getTime();
  const isDayFromNextMonth = singleDate.getMonth() > activeMonth;

  const isAvailableDay = availableDates
    ? availableDates?.includes(formattedSingleDate)
    : true;

  const color = useMemo(() => {
    if (isPastDay) {
      return "tertiary";
    }

    if (isSelected) {
      return "primary";
    }

    return "secondary";
  }, [isSelected, isPastDay]);

  if (isMobile && isPastDay) {
    return null;
  }

  const isDisabled = isPastDay || !isAvailableDay;

  return (
    <SingleDateCell
      aria-label={formattedSingleDate}
      color={color}
      isDayFromNextMonth={isDayFromNextMonth}
      isSelected={isSelected}
      data-testid={`${TEST_ID}${!isDisabled ? `--${testIdMap[color]}` : ""}`}
      onClick={() => {
        if (isSelected) {
          return onSelectedDate(undefined);
        }

        return onSelectedDate(singleDate.toISOString());
      }}
      disabled={isDisabled}
    >
      {singleDate.getDate()}
    </SingleDateCell>
  );
};

const SingleDateCell = styled(Button)<{
  isDayFromNextMonth?: boolean;
  isSelected?: boolean;
}>`
  font-weight: 400;
  border-radius: 8px;
  height: 72px;

  &.sr-button-md.sr-button-secondary {
    border: 0.5px solid ${tokens.OFFWHITE_40};

    padding: 16px;
  }

  &.sr-button-md.sr-button-secondary > span {
    color: ${(props) =>
      props.isDayFromNextMonth ? tokens.TINTS_OFF_WHITE_30 : undefined};
  }

  &.sr-button-md.sr-button-primary {
    padding: 16px;
  }

  &.sr-button-md.sr-button-secondary:hover {
    background: rgba(31, 38, 71, 0.1);
  }
`;

type SingleTimeSlotProps = {
  onSelectedDate: (date: string | undefined) => void;
  testId?: string;
  selectedDate: string;
  singleDate: Date;
  disabledPastDays?: boolean;
  availableDates?: string[];
  activeMonth: number;
  isMobile?: boolean;
};

export type { SingleTimeSlotProps };
export { SingleDaySlot };
